import type { EnhancedVerificationRequest } from '@common/types/treefort';
import { isValidEmail } from '@common/utils/isValidEmail';
import { useState } from 'react';
import { getPhoneValidationError } from '@/utils/phone';

export interface IDVClientForm {
    email: string;
    emailError: string;
    idVerification: boolean;
    bankReview: boolean;
    phone: string;
    countryCode: string;
    dialCode: number;
    phoneError: string;
}

function getFormFromRow(row: EnhancedVerificationRequest): IDVClientForm {
    return {
        email: row.email ?? '',
        emailError: '',
        idVerification: row.isSecondaryIdRequired ?? false,
        bankReview: row.isBankingVerificationRequired ?? false,
        phone: row.phoneNumber ?? '',
        countryCode: row.countryCode ?? '',
        dialCode: row.dialCode ?? 0,
        phoneError: '',
    };
}

export function useIDVClientForm(row: EnhancedVerificationRequest) {
    const [clientUpdate, setClientUpdate] = useState<IDVClientForm>(() => getFormFromRow(row));

    const handleClientDataPhoneChange = (phoneNumber: string) => {
        setClientUpdate((prev) => ({
            ...prev,
            phone: phoneNumber,
            phoneError: getPhoneValidationError(phoneNumber, prev.countryCode || 'ca', {
                emptyMessage: 'Phone number is required',
                invalidMessage: 'Enter a valid phone number',
            }),
        }));
    };

    const handleClientDataCountryChange = (countryData: { code: string; dialCode: string }) => {
        setClientUpdate((prev) => {
            const next = {
                ...prev,
                countryCode: countryData.code,
                dialCode: Number(countryData.dialCode),
            };

            if (!prev.phone.trim() && !prev.phoneError) {
                return next;
            }

            return {
                ...next,
                phoneError: getPhoneValidationError(prev.phone, countryData.code, {
                    emptyMessage: 'Phone number is required',
                    invalidMessage: 'Enter a valid phone number',
                }),
            };
        });
    };

    const handleEmailChange = (value: string) => {
        const error =
            value.trim() === ''
                ? 'Email is required'
                : !isValidEmail(value)
                  ? 'Enter a valid email address'
                  : '';

        setClientUpdate((prev) => ({
            ...prev,
            email: value,
            emailError: error,
        }));
    };

    const toggleCheckbox = (field: 'idVerification' | 'bankReview') => {
        setClientUpdate((prev) => ({
            ...prev,
            [field]: !prev[field],
        }));
    };

    return {
        clientUpdate,
        handleEmailChange,
        handleClientDataPhoneChange,
        handleClientDataCountryChange,
        toggleCheckbox,
    };
}
